/*
 * Copyright (c) 2024. Nedaware SL
 */

import {BasicEntity, BasicProperty} from "../../../basic-entity-back/annotations";
import {PropertyType} from "../../../basic-entity-back/property-type/property-type";
import {NestedBehavior} from "../../../basic-entity-back/property-type/nested-model-type";
import {Plazo} from "./plazo.model";
import {BaseEntity} from "../../base-entity.model";
import {Uri} from "../../../api/uri";
import {IRI_PROPERTY, TYPE_PROPERTY} from "../../../basic-entity-back/basic-entity-interface/mapping-internal";
import {CuentaBancariaCentro} from "../cuentas-bancarias/cuenta-bancaria-centro.model";
import {
    ManagerCachingStrategy,
    PropertyMap,
    ReadWrite
} from "../../../basic-entity-back/basic-entity-interface/mapping-external";
import {CentroProduccion} from "../../centros/centro-produccion.model";

@BasicEntity({
    name: 'Forma de pago',
    endpoint: 'forma_pagos',
    isPaginated: true,
    paginationSizes: [20, 30, 50],
    modelNameProperty: 'lectura',
    managerCaching: ManagerCachingStrategy.Lazy,
    type: 'FormaPago'
})
export class FormaPago extends BaseEntity {
    static readonly MEDOTOS_PAGO = {
        EFECTIVO: 'Efectivo',
        TRANSFERENCIA: 'Transferencia',
        ADEUDO_EN_CUENTA: 'Adeudo en cuenta',
        SALDO: 'Pagado con saldo',
    };

    @BasicProperty({
        isId: true,
        name: 'ID',
        type: PropertyType.Integer()
    })
    private _id: number;

    @BasicProperty({
        name: 'Nombre',
        type: PropertyType.String(),
        order: 1
    })
    private _nombre: string;

    @BasicProperty({
        name: 'Método Pago',
        type: PropertyType.Choice(...Object.values(FormaPago.MEDOTOS_PAGO)),
        order: 2
    })
    private _metodoPago: string;

    @BasicProperty({
        name: 'Plazos',
        type: PropertyType.NestedModel(Plazo, null, NestedBehavior.CreateOnly, [TYPE_PROPERTY, IRI_PROPERTY, 'formaPago', 'creadoPor', 'modificadoPor', 'version', 'fechaModificacion', 'bloqueadoPor']),
        array: true,
        order: 4
    })
    private _plazos;

    @BasicProperty({
        name: 'Cuenta Receptora',
        type: PropertyType.Uri(CuentaBancariaCentro),
        nullable: true,
        order: 3
    })
    private _cuentaBancariaReceptora: Uri;

    @BasicProperty({
        name: 'Requiere cuenta bancaria cliente',
        type: PropertyType.Boolean(),
        order: 4
    })
    private _requiereCuentaBancariaCliente = false;

    @BasicProperty({
        name: 'Centro de producción',
        type: PropertyType.Uri(CentroProduccion),
        nullable: true,
        order: 3
    })
    private _centroProduccion: Uri;

    get lectura() {
        return this._nombre;
    }

    get id(): number {
        return this._id;
    }

    set id(value: number) {
        this._id = value;
    }

    get nombre(): string {
        return this._nombre;
    }

    set nombre(value: string) {
        this._nombre = value;
    }

    get metodoPago(): string {
        return this._metodoPago;
    }

    set metodoPago(value: string) {
        this._metodoPago = value;
    }

    get plazos() {
        return this._plazos;
    }

    set plazos(value) {
        this._plazos = value;
    }

    get cuentaBancariaReceptora() {
        return this._cuentaBancariaReceptora;
    }

    set cuentaBancariaReceptora(value) {
        this._cuentaBancariaReceptora = value;
    }

    get requiereCuentaBancariaCliente(): boolean {
        return this._requiereCuentaBancariaCliente;
    }

    set requiereCuentaBancariaCliente(value: boolean) {
        this._requiereCuentaBancariaCliente = value;
    }

    get centroProduccion() {
        return this._centroProduccion;
    }

    set centroProduccion(value) {
        this._centroProduccion = value;
    }
}

Plazo.MAP_PLAZO.type = PropertyType.Uri(FormaPago);
